// ===========================================
// Waitlist review hooks (#374 release B, Phase 5) - owner only
// ===========================================
// Extend moves one booking's review day. Confirm lapses it: the booking is rejected, its enquiry
// may close, and a Held registration fee may be forfeited, so a success refreshes the Meet & Greet
// lists and calendar, the enquiries and the family payment history. Separate from useMeetAndGreet
// so the page's other tests (which mock that module) are unaffected.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { meetAndGreetApi } from '../api/meetAndGreet.api';
import { ENQUIRY_KEY_ROOT } from './enquiryKeyRoots';
import { familyPaymentKeys } from './useFamilyPayments';
import { invoiceKeys } from './useInvoices';
import { registrationFeeKeys } from './useRegistrationFeeSettings';
import { invalidateAfterMeetAndGreetWrite, invalidateLinkedEnquiries } from './useMeetAndGreet';

function useRefreshAfterWaitlistWrite() {
  const queryClient = useQueryClient();
  return () => {
    invalidateAfterMeetAndGreetWrite(queryClient);
    invalidateLinkedEnquiries(queryClient);
    queryClient.invalidateQueries({ queryKey: ENQUIRY_KEY_ROOT });
    queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    // A forfeited fee leaves the draft Edit's Held list (under invoiceKeys) and the switch preview (/qa P5 L12).
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    queryClient.invalidateQueries({ queryKey: registrationFeeKeys.all });
  };
}

export function useExtendWaitlistReview() {
  const refresh = useRefreshAfterWaitlistWrite();
  return useMutation({
    mutationFn: ({ id, reviewOn, note }: { id: string; reviewOn: string; note?: string }) =>
      meetAndGreetApi.extendWaitlist(id, { reviewOn, ...(note ? { note } : {}) }),
    // Settled, not success: a 409 (it left the waitlist) means the page is stale too.
    onSettled: refresh,
  });
}

export function useConfirmWaitlistLapsed() {
  const refresh = useRefreshAfterWaitlistWrite();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => meetAndGreetApi.confirmWaitlistLapsed(id),
    onSettled: refresh,
  });
}
